/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {Component, HostBinding, Input, OnDestroy, OnInit} from '@angular/core';
import {EditorView} from 'prosemirror-view';
import {Subscription} from 'rxjs';
import Icon from '../../../icons';
import {ToggleCommands} from '../MenuCommands';
import {FcAgEditorService} from '../../../editor.service';
import {MenuService} from '../menu.service';
import {TBItems, ToolbarItem} from '../../../types';

/**
 * Toggle command component.
 */
@Component({
    selector: 'fc-ag-toggle-command',
    templateUrl: './toggle-command.component.html',
    styleUrls: ['./toggle-command.component.scss']
})

export class ToggleCommandComponent implements OnInit, OnDestroy {
    /**
     * Toolbar item.
     */
    @Input() toolbarItem: ToolbarItem;

    /**
     * Getter for name.
     * 
     * @returns TBItems.
     */
    get name(): TBItems {
        return this.toolbarItem as TBItems;
    }

    /**
     * Html.
     */
    html: string;

    /**
     * Editor view.
     */
    editorView: EditorView;

    private updateSubscription: Subscription;

    /**
     * Default constructor.
     * 
     * @param fcAgEditorService FcAgEditorService.
     * @param menuService Menu service.
     */
    constructor(
        private fcAgEditorService: FcAgEditorService,
        private menuService: MenuService
    ) {
    }

    /**
     * Is active binding.
     */
    @HostBinding('class.FcAgEditor__MenuItem--Active') isActive = false;

    /**
     * Disabled binding.
     */
    @HostBinding('class.FcAgEditor--Disabled') disabled = false;

    /**
     * Toggle command.
     * 
     * @param e Mouse event.
     */
    toggle(e: MouseEvent): void {
        e.preventDefault();

        if (e.button !== 0) {
            return;
        }

        const {state, dispatch} = this.editorView;
        const command = ToggleCommands[this.name];
        command.toggle()(state, dispatch);
    }

    /**
     * Update view.
     * 
     * @param view Editor view.
     */
    update = (view: EditorView): void => {
        const {state} = view;
        const command = ToggleCommands[this.name];
        this.isActive = command.isActive(state);
        this.disabled = !command.canExecute(state);
    }

    /**
     * Get title.
     * 
     * @param name String.
     * @returns String.
     */
    getTitle(name: string): string {
        return this.fcAgEditorService.locals.get(name);
    }

    /**
     * Angular life cycle.
     */
    ngOnInit(): void {
        this.html = Icon.get(this.name);

        this.editorView = this.menuService.editor.view;

        this.updateSubscription = this.menuService.editor.update.subscribe((view: EditorView) => {
            this.update(view);
        });
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.updateSubscription.unsubscribe();
    }
}
